import { createClient } from "@supabase/supabase-js";

// Public Supabase credentials. The anon key is NOT a secret — it is gated by
// row-level security and is inlined into the public client bundle regardless.
// We hardcode the known-good values as a fallback so the app keeps working even
// when the build-time env var is MISSING or set to a stale/invalid/truncated
// value in the hosting provider (a plain `env || fallback` can't catch that,
// because a wrong-but-non-empty value would still "win"). This mirrors the
// edge function (netlify/edge-functions/inject-meta.ts).
const SUPABASE_URL_FALLBACK = "https://gupqexwrdnojbmyiwdjp.supabase.co";
const SUPABASE_ANON_KEY_FALLBACK =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd1cHFleHdyZG5vamJteWl3ZGpwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE0NTMzNDQsImV4cCI6MjA4NzAyOTM0NH0.M6IHUxp894tym6AsYGgRaqX4XQfPXrE2gSdJ-RAnXTI";

const PROJECT_REF = "gupqexwrdnojbmyiwdjp";

// Accept an env-provided key ONLY if it is a well-formed JWT whose payload is
// this project's `anon` key. This rejects every realistic misconfiguration:
// empty, truncated, whitespace-corrupted, a key from the wrong project, or a
// service-role key pasted by mistake — all fall back to the known-good value.
function isValidAnonKey(token: string): boolean {
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    const json = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(json)) as { ref?: string; role?: string };
    return payload.ref === PROJECT_REF && payload.role === "anon";
  } catch {
    return false;
  }
}

const envUrl = (import.meta.env.VITE_SUPABASE_URL ?? "").trim();
const envKey = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? "").trim();

const supabaseUrl = /^https:\/\/.+\.supabase\.co\/?$/.test(envUrl)
  ? envUrl
  : SUPABASE_URL_FALLBACK;
const supabaseAnonKey = isValidAnonKey(envKey)
  ? envKey
  : SUPABASE_ANON_KEY_FALLBACK;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Authenticated fetch — attaches the current Supabase session token
 * as an Authorization header to API requests.
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(options.headers);
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(url, { ...options, headers });
}
